/**
 * UnifiedImageGrid Component
 * A single responsive image grid that combines cover photo selection
 * with confirmed and pending tag display.
 */

import { useCallback, useRef, useEffect, useState } from 'react';
import { RefreshCw, Loader2, ListChecks, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { ImageCard } from './ImageCard';
import type { LocationImage, TagRunUsage, TagSuggestion } from '@/types/api';
import { formatTagRunsTotal } from '@/lib/ai-run-usage';

interface UnifiedImageGridProps {
  /** Array of partner images to display */
  images: LocationImage[];
  /** Currently selected cover image order (matches image.order, NOT array index) */
  selectedCoverOrder: number | null;
  /** Callback when cover photo is changed */
  onCoverChange: (order: number) => void;
  /** Confirmed image tags by image index (string key) */
  confirmedImageTags: Record<string, number[]>;
  /** Manually-added image tags by image index (Phase 26) */
  manualImageTags?: Record<string, number[]>;
  /** Pending image tag suggestions by image index (string key) */
  imageSuggestions: Record<string, TagSuggestion[]>;
  /** The latest tag run of each photo by image index (FR-142) */
  imageRuns?: Record<string, TagRunUsage>;
  /** All suggestions (to look up tag names for confirmed tags) */
  allSuggestions: {
    location?: TagSuggestion[];
    images?: Record<string, TagSuggestion[]>;
  };
  /** Available tags (for looking up names of manual tags) */
  availableTags?: { id: number; name: string }[];
  /** IDs of rejected tags (to filter out from pending) */
  rejectedTags: number[];
  /** Set of tag IDs currently being processed */
  processingTags: Set<number>;
  /** Set of tag IDs currently being removed (Phase 26) */
  removingTags?: Set<number>;
  /** Handler for accepting an image tag */
  onAcceptTag: (tagId: number, imageIndex: number) => void;
  /** Handler for rejecting an image tag */
  onRejectTag: (tagId: number, imageIndex: number) => void;
  /** Handler for removing a confirmed/manual image tag (Phase 26) */
  onRemoveTag?: (tagId: number, imageIndex: number) => void;
  /** Callback to view image full-size */
  onImageView?: (index: number) => void;
  /** Whether the grid is loading */
  isLoading?: boolean;
  /** Whether interactions are disabled */
  disabled?: boolean;
  /** Location ID for manual tag addition (Phase 26) */
  locationId?: string;
  /** Callback when a tag is manually added to an image (Phase 26) */
  onTagAdded?: () => void;
  /** Handler for re-analyzing all images */
  onReanalyzeAllImages?: () => void;
  /** Whether re-analyze all images is in progress */
  isReanalyzingAllImages?: boolean;
  /** Handler for re-analyzing a single image */
  onReanalyzeSingleImage?: (imageIndex: number) => void;
  /** Whether any single image re-analysis is in progress */
  isReanalyzingSingleImage?: boolean;
  /** Index of the image currently being re-analyzed (if any) */
  reanalyzingImageIndex?: number;
  /**
   * Handler for removing an image, called with the URL of the image whose
   * remove was confirmed (not its index, which a refetch can shift while the
   * confirm dialog is open). Absent = no remove control.
   */
  onRemoveImage?: (imageUrl: string) => void;
  /** Index of the image whose removal is in flight (if any). */
  removingImageIndex?: number;
  /** When set, every remove button is disabled and shows this reason. */
  removeDisabledReason?: string;
  /**
   * Handler for removing several images at once, called with their URLs in
   * gallery order once the admin confirms. Absent = no selection mode.
   */
  onRemoveImages?: (imageUrls: string[]) => void;
  /** Progress of a multi-photo removal in flight (if any). */
  bulkRemovalProgress?: { done: number; total: number };
}

/**
 * Look up tag info from suggestions by tag ID.
 */
function getTagInfo(
  tagId: number,
  suggestions: TagSuggestion[]
): { tagId: number; tagName: string; isAiSuggested: boolean } | null {
  const suggestion = suggestions.find((s) => s.tagId === tagId);
  if (suggestion) {
    return { tagId, tagName: suggestion.tagName, isAiSuggested: true };
  }
  return null;
}

/**
 * Get number of columns based on viewport width.
 */
function getColumnsForWidth(): number {
  if (typeof window === 'undefined') return 3;
  if (window.innerWidth >= 1024) return 3;
  if (window.innerWidth >= 768) return 2;
  return 1;
}

/**
 * UnifiedImageGrid displays a responsive grid of images with:
 * - Cover photo selection
 * - Confirmed tags displayed on each image
 * - Pending tag suggestions with accept/reject actions
 * - Keyboard navigation (arrow keys, Enter/Space)
 */
export function UnifiedImageGrid({
  images,
  selectedCoverOrder,
  onCoverChange,
  confirmedImageTags,
  manualImageTags,
  imageSuggestions,
  imageRuns,
  allSuggestions,
  availableTags,
  rejectedTags,
  processingTags,
  removingTags,
  onAcceptTag,
  onRejectTag,
  onRemoveTag,
  onImageView,
  isLoading = false,
  disabled = false,
  locationId,
  onTagAdded,
  onReanalyzeAllImages,
  isReanalyzingAllImages = false,
  onReanalyzeSingleImage,
  isReanalyzingSingleImage = false,
  reanalyzingImageIndex,
  onRemoveImage,
  removingImageIndex,
  removeDisabledReason,
  onRemoveImages,
  bulkRemovalProgress,
}: UnifiedImageGridProps): JSX.Element {
  const gridRef = useRef<HTMLDivElement>(null);
  // Multi-photo removal. Images are selected by URL, their stable identity: a
  // refetch can shift indexes, and a URL that left the gallery is simply not counted.
  const [selectionMode, setSelectionMode] = useState(false);
  const [checkedUrls, setCheckedUrls] = useState<ReadonlySet<string>>(new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const checkedImages = images.filter((image) => checkedUrls.has(image.url));
  const bulkRemoveDisabled = disabled || checkedImages.length === 0 || removeDisabledReason !== undefined;

  const toggleChecked = useCallback((url: string) => {
    setCheckedUrls((prev) => {
      const next = new Set(prev);
      if (next.has(url)) {
        next.delete(url);
      } else {
        next.add(url);
      }
      return next;
    });
  }, []);

  const exitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setCheckedUrls(new Set());
  }, []);
  // Only the photos still in the gallery count, and not one whose run a re-analysis is replacing
  // (its card hides that run too); while every photo is re-analysed there is no total.
  const runsTotal = isReanalyzingAllImages
    ? null
    : formatTagRunsTotal(
        images
          .map((_, index) =>
            isReanalyzingSingleImage && reanalyzingImageIndex === index ? undefined : imageRuns?.[String(index)]
          )
          .filter((run): run is TagRunUsage => run !== undefined)
      );

  // Find the array index for the selected order value
  const selectedArrayIndex = selectedCoverOrder === null
    ? 0
    : images.findIndex((img) => img.order === selectedCoverOrder);
  const effectiveSelectedIndex = selectedArrayIndex === -1 ? 0 : selectedArrayIndex;

  const focusedIndexRef = useRef<number>(effectiveSelectedIndex);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, currentIndex: number) => {
      if (disabled || images.length === 0) return;

      // In selection mode Enter and Space on the focused card toggle its checkbox (cover and viewer
      // are hidden); on a control inside the card they keep that control's own meaning.
      const onCard = event.target instanceof HTMLElement && event.target.getAttribute('role') === 'option';
      if (selectionMode && (event.key === 'Enter' || event.key === ' ')) {
        const toggledImage = images[currentIndex];
        if (onCard && toggledImage) {
          event.preventDefault();
          toggleChecked(toggledImage.url);
        }
        return;
      }

      let newIndex: number;
      const cols = getColumnsForWidth();

      switch (event.key) {
        case 'ArrowRight':
          event.preventDefault();
          newIndex = Math.min(currentIndex + 1, images.length - 1);
          break;
        case 'ArrowLeft':
          event.preventDefault();
          newIndex = Math.max(currentIndex - 1, 0);
          break;
        case 'ArrowDown':
          event.preventDefault();
          newIndex = Math.min(currentIndex + cols, images.length - 1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          newIndex = Math.max(currentIndex - cols, 0);
          break;
        case 'Enter': {
          event.preventDefault();
          const enterImage = images[currentIndex];
          if (enterImage) {
            onCoverChange(enterImage.order);
          }
          return;
        }
        case ' ': {
          event.preventDefault();
          const spaceImage = images[currentIndex];
          if (onImageView) {
            onImageView(currentIndex);
          } else if (spaceImage) {
            onCoverChange(spaceImage.order);
          }
          return;
        }
        case 'Home':
          event.preventDefault();
          newIndex = 0;
          break;
        case 'End':
          event.preventDefault();
          newIndex = images.length - 1;
          break;
        default:
          return;
      }

      if (newIndex !== currentIndex) {
        focusedIndexRef.current = newIndex;
        const items = gridRef.current?.querySelectorAll('[role="option"]');
        const item = items?.[newIndex];
        if (item instanceof HTMLElement) {
          item.focus();
        }
      }
    },
    [disabled, images, onCoverChange, onImageView, selectionMode, toggleChecked]
  );

  // Set up initial focusability
  useEffect(() => {
    if (!isLoading && images.length > 0 && gridRef.current) {
      const firstItem = gridRef.current.querySelector('[role="option"]');
      if (firstItem instanceof HTMLElement) {
        firstItem.tabIndex = 0;
      }
    }
  }, [isLoading, images.length]);

  // Build confirmed tags lookup for each image (including AI-confirmed and manual tags)
  const getConfirmedTagsForImage = (imageIndex: number): { tagId: number; tagName: string; isAiSuggested: boolean }[] => {
    const aiConfirmedTagIds = confirmedImageTags[String(imageIndex)] ?? [];
    const manualTagIds = manualImageTags?.[String(imageIndex)] ?? [];
    const imageSuggestionsForIndex = allSuggestions.images?.[String(imageIndex)] ?? [];
    const locationSuggestions = allSuggestions.location ?? [];

    // Build a lookup map from available tags for manual tag names
    const availableTagMap = new Map<number, string>();
    (availableTags ?? []).forEach((tag) => {
      availableTagMap.set(tag.id, tag.name);
    });

    // AI-confirmed tags
    const aiTags = aiConfirmedTagIds
      .map((tagId) => {
        // First check image suggestions, fallback to location suggestions
        const info = getTagInfo(tagId, imageSuggestionsForIndex)
          ?? getTagInfo(tagId, locationSuggestions);
        return info;
      })
      .filter((tag): tag is { tagId: number; tagName: string; isAiSuggested: boolean } => tag !== null);

    // Manual tags (Phase 26)
    const manualTags = manualTagIds
      .map((tagId) => {
        // First check suggestions (in case tag was also suggested), then available tags
        const suggestionInfo = getTagInfo(tagId, imageSuggestionsForIndex)
          ?? getTagInfo(tagId, locationSuggestions);
        if (suggestionInfo) {
          return { ...suggestionInfo, isAiSuggested: false }; // Manual = not AI-suggested
        }
        // Fallback to available tags lookup
        const tagName = availableTagMap.get(tagId);
        return tagName ? { tagId, tagName, isAiSuggested: false } : null;
      })
      .filter((tag): tag is { tagId: number; tagName: string; isAiSuggested: boolean } => tag !== null);

    return [...aiTags, ...manualTags];
  };

  // Get pending tags for an image (filtering out confirmed and rejected)
  const getPendingTagsForImage = (imageIndex: number): TagSuggestion[] => {
    const suggestions = imageSuggestions[String(imageIndex)] ?? [];
    const confirmed = confirmedImageTags[String(imageIndex)] ?? [];

    return suggestions.filter(
      (tag) => !confirmed.includes(tag.tagId) && !rejectedTags.includes(tag.tagId)
    );
  };

  // Get excluded tag IDs for the TagSelector (Phase 26)
  // This includes all confirmed, manual, and pending tags for a given image
  const getExcludeTagIdsForImage = (imageIndex: number): number[] => {
    const confirmed = confirmedImageTags[String(imageIndex)] ?? [];
    const manual = manualImageTags?.[String(imageIndex)] ?? [];
    const pending = getPendingTagsForImage(imageIndex).map((t) => t.tagId);
    return [...confirmed, ...manual, ...pending];
  };

  // Don't render if no images
  if (!isLoading && images.length === 0) {
    return <></>;
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-3">
        <Label>Images</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton
              key={`skeleton-${String(i)}`}
              className="aspect-[3/2] rounded-lg"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label id="unified-image-grid-label">Images</Label>
          <div className="flex flex-wrap items-center gap-2">
            {runsTotal && (
              <span
                className="text-xs text-muted-foreground"
                title="Cost of the latest successful tag run of each photo shown. Earlier and failed runs are in the AI Router ledger."
                data-testid="image-tag-runs-total"
              >
                {runsTotal}
              </span>
            )}
            <span className="text-sm text-muted-foreground">
              {String(images.length)} {images.length === 1 ? 'image' : 'images'}
            </span>
            {onRemoveImages && (
              <Button
                type="button"
                variant={selectionMode ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => { if (selectionMode) { exitSelectionMode(); } else { setSelectionMode(true); } }}
                disabled={disabled && !selectionMode}
                className="h-8 px-2 gap-1"
                aria-pressed={selectionMode}
                data-testid="bulk-select-toggle"
              >
                <ListChecks className="h-4 w-4" />
                {selectionMode ? 'Done' : 'Select'}
              </Button>
            )}
            {onReanalyzeAllImages && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={onReanalyzeAllImages}
                    disabled={disabled || isReanalyzingAllImages || isReanalyzingSingleImage}
                    className="h-8 px-2"
                    data-testid="reanalyze-all-images-btn"
                  >
                    {isReanalyzingAllImages ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Re-analyze all images</TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>

        {bulkRemovalProgress && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status" aria-live="polite" data-testid="bulk-remove-progress">
            <Loader2 className="h-4 w-4 motion-safe:animate-spin" aria-hidden="true" />
            Removing image {String(Math.min(bulkRemovalProgress.done + 1, bulkRemovalProgress.total))} of {String(bulkRemovalProgress.total)}…
          </p>
        )}

        {selectionMode && (
          <div
            className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 px-3 py-2"
            data-testid="bulk-select-toolbar"
          >
            <span className="text-sm font-medium" data-testid="bulk-selected-count">
              {String(checkedImages.length)} selected
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 px-2"
              disabled={disabled}
              onClick={() => {
                setCheckedUrls(
                  checkedImages.length === images.length ? new Set() : new Set(images.map((image) => image.url))
                );
              }}
              data-testid="bulk-select-all"
            >
              {checkedImages.length === images.length ? 'Clear selection' : 'Select all'}
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="h-8 px-2 gap-1 ml-auto"
              disabled={bulkRemoveDisabled}
              title={removeDisabledReason}
              onClick={() => { setBulkConfirmOpen(true); }}
              data-testid="bulk-remove-btn"
            >
              <Trash2 className="h-4 w-4" />
              Remove selected
            </Button>
            {removeDisabledReason && (
              <span className="w-full text-xs text-muted-foreground">{removeDisabledReason}</span>
            )}
          </div>
        )}

        <p className="text-sm text-muted-foreground">
          {selectionMode
            ? 'Click images or tick their boxes to select them for removal.'
            : onImageView
            ? 'Click an image to view full size. Hover and click "Set as Cover" to select the cover photo. Accept or reject AI tag suggestions.'
            : 'Select which image will be shown as the cover photo on ScoutAround.'}
          {!selectionMode && selectedCoverOrder === null && ' The first image is used as cover by default.'}
        </p>

        {/* Responsive image grid */}
        <div
          ref={gridRef}
          role="listbox"
          aria-labelledby="unified-image-grid-label"
          aria-activedescendant={`unified-image-${String(effectiveSelectedIndex + 1)}`}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[600px] overflow-y-auto p-1"
          data-testid="unified-image-grid"
        >
          {images.map((image, index) => {
            const isSelected = index === effectiveSelectedIndex;
            const isFirst = index === 0;
            const isDefaultCover = isFirst && selectedCoverOrder === null;

            return (
              <div
                key={`image-${String(index)}`}
                onKeyDown={(e) => { handleKeyDown(e, index); }}
              >
                <ImageCard
                  image={image}
                  index={index}
                  isSelected={isSelected}
                  isDefaultCover={isDefaultCover && !isSelected}
                  confirmedTags={getConfirmedTagsForImage(index)}
                  pendingTags={getPendingTagsForImage(index)}
                  processingTagIds={processingTags}
                  removingTagIds={removingTags}
                  rejectedTagIds={rejectedTags}
                  onSelectCover={() => { onCoverChange(image.order); }}
                  onAcceptTag={(tagId) => { onAcceptTag(tagId, index); }}
                  onRejectTag={(tagId) => { onRejectTag(tagId, index); }}
                  onRemoveTag={onRemoveTag ? (tagId) => { onRemoveTag(tagId, index); } : undefined}
                  onImageView={onImageView ? () => { onImageView(index); } : undefined}
                  disabled={disabled}
                  locationId={locationId}
                  excludeTagIds={getExcludeTagIdsForImage(index)}
                  onTagAdded={onTagAdded}
                  onReanalyzeImage={onReanalyzeSingleImage ? () => { onReanalyzeSingleImage(index); } : undefined}
                  isReanalyzingImage={isReanalyzingSingleImage && reanalyzingImageIndex === index}
                  onRemoveImage={onRemoveImage}
                  isRemovingImage={removingImageIndex === index}
                  removeDisabledReason={removeDisabledReason}
                  tagRun={imageRuns?.[String(index)]}
                  selectionMode={selectionMode}
                  isChecked={checkedUrls.has(image.url)}
                  onToggleChecked={() => { toggleChecked(image.url); }}
                />
              </div>
            );
          })}
        </div>

        {onRemoveImages && (
          <AlertDialog open={bulkConfirmOpen} onOpenChange={setBulkConfirmOpen}>
            <AlertDialogContent data-testid="bulk-remove-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Remove {String(checkedImages.length)} {checkedImages.length === 1 ? 'image' : 'images'}?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Images {checkedImages.map((image) => String(images.indexOf(image) + 1)).join(', ')} are removed from
                  this listing in the Bridge and, if the listing is published, from ScoutAround. Uploading the file
                  again will not bring them back.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="bulk-remove-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="bulk-remove-confirm"
                  disabled={bulkRemoveDisabled}
                  onClick={() => {
                    const urls = checkedImages.map((image) => image.url);
                    exitSelectionMode();
                    if (urls.length > 0) onRemoveImages(urls);
                  }}
                >
                  Remove {String(checkedImages.length)}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </TooltipProvider>
  );
}
